/** The cave view: the map in its colours and real sprites, at 1x or 1/2x, with grid, cursor and shape preview. */

import { BLACK, BLUE, DARK_GREY, GREY, LIGHT_BLUE, LIGHT_GREY, WHITE, YELLOW } from '../../render/palette';
import type { Point } from '../model/grid';
import { clampView, scrollBy, viewOrigin, visibleSpan } from '../model/view';
import type { KitApi } from './api';
import { VIEW } from './layout';
import { drawMark, drawSprite } from './look';
import { dither } from './widgets';

export interface ViewExtras {
  /** What to show in each cell: the map, or the map with `?` rolled. */
  readonly cells: readonly string[];
  /** Show the cursor box (the pointer is over the view, or the keyboard is driving). */
  readonly cursor: boolean;
}

function scrollBars(k: KitApi): void {
  const { ui, st } = k;
  const s = ui.screen;
  const g = st.doc.grid;
  const span = visibleSpan(k.view, g, VIEW);
  const bottom = VIEW.y + VIEW.h - 3;
  const right = VIEW.x + VIEW.w - 3;
  // A click on a bar centres the view on that point of the cave.
  const jump = (axis: 'x' | 'y', at: number): void => {
    const v = k.view;
    if (axis === 'x') k.view = clampView({ ...v, sx: ((at - VIEW.x) / VIEW.w) * g.w * v.cell - VIEW.w / 2 }, g, VIEW);
    else k.view = clampView({ ...v, sy: ((at - VIEW.y) / VIEW.h) * g.h * v.cell - VIEW.h / 2 }, g, VIEW);
  };
  if (span.x1 - span.x0 < 1) {
    s.fill(VIEW.x, bottom, VIEW.w, 3, BLACK);
    s.fill(VIEW.x, bottom + 1, VIEW.w, 2, DARK_GREY);
    s.fill(VIEW.x + span.x0 * VIEW.w, bottom + 1, Math.max(4, (span.x1 - span.x0) * VIEW.w), 2, LIGHT_GREY);
    ui.add({
      x: VIEW.x, y: bottom - 3, w: VIEW.w, h: 7, id: 'hbar', tip: 'SCROLL SIDEWAYS: WHEEL HERE, OR CLICK',
      wheel: (d) => void (k.view = scrollBy(k.view, g, VIEW, d * 4 * k.view.cell, 0)), click: () => jump('x', ui.px),
    });
  }
  if (span.y1 - span.y0 < 1) {
    s.fill(right, VIEW.y, 3, VIEW.h, BLACK);
    s.fill(right + 1, VIEW.y, 2, VIEW.h, DARK_GREY);
    s.fill(right + 1, VIEW.y + span.y0 * VIEW.h, 2, Math.max(4, (span.y1 - span.y0) * VIEW.h), LIGHT_GREY);
    ui.add({
      x: right - 3, y: VIEW.y, w: 7, h: VIEW.h, id: 'vbar', tip: 'SCROLL: WHEEL OR CLICK',
      click: () => jump('y', ui.py),
    });
  }
}

function gridLines(k: KitApi, ox: number, oy: number, x0: number, x1: number, y0: number, y1: number): void {
  const s = k.ui.screen;
  const c = k.view.cell;
  const top = Math.max(VIEW.y, oy + y0 * c);
  const bottom = Math.min(VIEW.y + VIEW.h, oy + (y1 + 1) * c);
  const left = Math.max(VIEW.x, ox + x0 * c);
  const right = Math.min(VIEW.x + VIEW.w, ox + (x1 + 1) * c);
  for (let x = x0; x <= x1 + 1; x++) s.fill(ox + x * c, top, 1, bottom - top, x % 10 === 0 ? GREY : DARK_GREY);
  for (let y = y0; y <= y1 + 1; y++) s.fill(left, oy + y * c, right - left, 1, y % 10 === 0 ? GREY : DARK_GREY);
}

/** A two-tone box round cells a..b (inclusive), easy to see on any sprite. */
function cellBox(k: KitApi, ox: number, oy: number, a: Point, b: Point, ink: number): void {
  const s = k.ui.screen;
  const c = k.view.cell;
  const x = ox + Math.min(a.x, b.x) * c;
  const y = oy + Math.min(a.y, b.y) * c;
  const w = (Math.abs(a.x - b.x) + 1) * c;
  const h = (Math.abs(a.y - b.y) + 1) * c;
  s.box(x - 2, y - 2, w + 4, h + 4, BLACK);
  s.box(x - 1, y - 1, w + 2, h + 2, ink);
}

export function drawView(k: KitApi, extras: ViewExtras): void {
  const { ui, st } = k;
  const s = ui.screen;
  const g = st.doc.grid;
  const c = k.view.cell;
  s.box(VIEW.x - 1, VIEW.y - 1, VIEW.w + 2, VIEW.h + 2, BLUE);
  const ctx = s.ctx;
  ctx.save();
  ctx.beginPath();
  ctx.rect(VIEW.x, VIEW.y, VIEW.w, VIEW.h);
  ctx.clip();
  const o = viewOrigin(k.view, g, VIEW);
  const ox = VIEW.x + o.x;
  const oy = VIEW.y + o.y;
  dither(s, VIEW, DARK_GREY);
  s.fill(ox, oy, g.w * c, g.h * c, BLACK);
  const x0 = Math.max(0, Math.floor(-o.x / c));
  const y0 = Math.max(0, Math.floor(-o.y / c));
  const x1 = Math.min(g.w - 1, Math.floor((VIEW.w - 1 - o.x) / c));
  const y1 = Math.min(g.h - 1, Math.floor((VIEW.h - 1 - o.y) / c));

  // The shape being dragged shows as what it will paint.
  const shape = k.painter.preview();
  const cells = shape ? [...extras.cells] : extras.cells;
  if (shape) {
    for (const p of shape.points) {
      if (p.x >= 0 && p.y >= 0 && p.x < g.w && p.y < g.h) (cells as string[])[p.y * g.w + p.x] = shape.ch;
    }
  }

  ctx.save();
  ctx.translate(ox, oy);
  ctx.scale(c / 16, c / 16);
  const atlas = k.atlas();
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) drawSprite(ctx, atlas, cells[y * g.w + x] ?? 'W', x * 16, y * 16, ui.anim);
  }
  ctx.restore();
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) drawMark(s, cells[y * g.w + x] ?? 'W', ox + x * c, oy + y * c, c);
  }
  if (k.showGrid) gridLines(k, ox, oy, x0, x1, y0, y1);

  const stroke = k.painter.stroke;
  if (stroke && shape) {
    cellBox(k, ox, oy, stroke.anchor, stroke.tool === 'line' ? stroke.anchor : stroke.last, LIGHT_BLUE);
  }
  if (extras.cursor) cellBox(k, ox, oy, k.cursor, k.cursor, (ui.anim >> 3) % 2 === 0 ? YELLOW : WHITE);
  ctx.restore();

  ui.add({
    ...VIEW, id: 'view',
    wheel: (d) => {
      const tall = g.h * c > VIEW.h;
      k.view = scrollBy(k.view, g, VIEW, tall ? 0 : d * 3 * c, tall ? d * 3 * c : 0);
    },
  });
  scrollBars(k);
}
